import React, { useCallback, useMemo } from "react";
import { FiRefreshCcw, FiFilter } from "react-icons/fi";
import { useInvalidate } from "../../../../hooks/useInvalidate";
import Divider from "../../../../components/shared/Divider/Divider";
import { Pagination } from "../../../../components/shared/Pagination/Pagination";
import Button from "../../../../components/shared/Button/Button";
import SelectMulti from "../../../../components/shared/Select/SelectMulti";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchVideos } from "../../hooks/useAdminSearchVideos";
import { downloadVideosHelper } from "../../../swing_library/utils/download";
import { VideoFiltersPanel } from "./VideoFiltersPanel";
import { VIDEOS_FIXED_COLUMNS, videosColumnOptions } from "./constants/columns";
import { createVideosColumnDefinitions } from "./VideoTableColumns";

export default function VideosView() {
  const invalidate = useInvalidate();
  const {
    videosState,
    setVideosPagination,
    setVideosSort,
    setVideosVisibleColumns,
    setVideosFiltersOpen,
  } = useAdminSearchStore();

  const { filters, pagination, sort, order, visibleColumns, filtersOpen } =
    videosState;

  const { data, isLoading, error } = useAdminSearchVideos();
  const videos = data?.videos || [];
  const total = data?.total || 0;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (pagination.limit || 1))),
    [total, pagination.limit],
  );

  const onPageChange = useCallback(
    async (page) => {
      const clamped = Math.min(Math.max(page, 1), totalPages);
      setVideosPagination({ page: clamped });
    },
    [totalPages, setVideosPagination],
  );

  const onRowsPerPageChange = useCallback(
    async (n) => {
      setVideosPagination({ page: 1, limit: n });
    },
    [setVideosPagination],
  );

  const onRefresh = useCallback(() => {
    invalidate(["adminSearchVideos_key"]);
  }, [invalidate]);

  const handleFiltersSubmit = useCallback(() => {
    setVideosPagination({ page: 1 });
    setVideosFiltersOpen(false);
    invalidate(["adminSearchVideos_key"]);
  }, [invalidate, setVideosFiltersOpen, setVideosPagination]);

  const onSortVideoCreated = useCallback(
    (columnKey) => {
      if (columnKey !== "videoCreated") return;
      setVideosPagination({ page: 1 });
      if (sort === "videoCreated") {
        setVideosSort("videoCreated", order === "asc" ? "desc" : "asc");
      } else {
        setVideosSort("videoCreated", "asc");
      }
    },
    [sort, order, setVideosSort, setVideosPagination],
  );

  const handleDownload = useCallback((video) => {
    const url = video?.videoPath || video?.downloadUrl || video?.videoUrl;
    if (!url) return;
    const normalized = {
      ...video,
      videoPath: url,
      videoOrigName: video?.videoOrigName ?? video?.fileName ?? video?.name ?? "video",
    };
    downloadVideosHelper({ [video?.id ?? url]: normalized });
  }, []);

  const columnHandlers = useMemo(
    () => ({
      onDownloadVideo: handleDownload,
    }),
    [handleDownload],
  );

  const columnDefinitions = useMemo(
    () =>
      createVideosColumnDefinitions({
        sort,
        order,
        onSort: onSortVideoCreated,
      }),
    [sort, order, onSortVideoCreated],
  );

  const visibleColumnDefs = useMemo(() => {
    const fixedCols = VIDEOS_FIXED_COLUMNS.map(
      (colKey) => columnDefinitions[colKey],
    ).filter(Boolean);
    const otherCols = visibleColumns
      .filter(
        (colKey) =>
          !VIDEOS_FIXED_COLUMNS.includes(colKey) && columnDefinitions[colKey],
      )
      .map((colKey) => columnDefinitions[colKey]);
    return [...fixedCols, ...otherCols];
  }, [visibleColumns, columnDefinitions]);

  const columnCount = visibleColumnDefs.length || 1;

  const hasActiveFilters = useMemo(() => {
    if ((filters.userInput || "").trim()) return true;
    if ((filters.videoSource || "").trim()) return true;
    if ((filters.videoType || "").trim()) return true;
    if ((filters.cameraAngle || "").trim()) return true;
    if (filters.videoCreated?.from || filters.videoCreated?.to) return true;
    if (Array.isArray(filters.golfClub) && filters.golfClub.length > 0)
      return true;
    return false;
  }, [filters]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={() => setVideosFiltersOpen(true)}
          color={hasActiveFilters ? "primary50" : "white/gray300"}
          size="md"
          rounded="lg"
          startContent={<FiFilter />}
          title="Open filters"
        >
          Filters
        </Button>
        <SelectMulti
          header="Columns"
          className="min-w-[192px]"
          onChange={(val) => {
            if (VIDEOS_FIXED_COLUMNS.includes(val)) return;
            setVideosVisibleColumns((prev = VIDEOS_FIXED_COLUMNS) => {
              const filtered = prev.filter(
                (v) => !VIDEOS_FIXED_COLUMNS.includes(v),
              );
              return filtered.includes(val)
                ? filtered.filter((v) => v !== val)
                : [...filtered, val];
            });
          }}
          onClear={() => setVideosVisibleColumns([...VIDEOS_FIXED_COLUMNS])}
          options={videosColumnOptions}
          placeholder="Select columns..."
          values={(visibleColumns || []).filter(
            (v) => !VIDEOS_FIXED_COLUMNS.includes(v),
          )}
          buttonProps={{
            size: "md",
            rounded: "lg",
            color:
              (visibleColumns || []).filter(
                (v) => !VIDEOS_FIXED_COLUMNS.includes(v),
              ).length > 0
                ? "primary50"
                : "white/gray300",
          }}
        />
        <Button
          type="button"
          isDisabled={isLoading}
          onClick={onRefresh}
          color="white/gray300"
          size="md"
          rounded="lg"
          startContent={<FiRefreshCcw />}
          title="Refresh"
        >
          Refresh
        </Button>
      </div>

      <VideoFiltersPanel
        isOpen={filtersOpen}
        onClose={() => setVideosFiltersOpen(false)}
        onSubmit={handleFiltersSubmit}
      />

      <div className="overflow-hidden rounded-xl border border-gray200">
        <div className="overflow-x-auto min-h-80">
          <table className="min-w-full text-left text-base">
            <thead className="bg-gray50 text-sm uppercase tracking-wide text-gray600">
              <tr>{visibleColumnDefs.map((col) => col.renderHeader())}</tr>
            </thead>

            <tbody className="divide-y divide-gray200">
              {error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-red-700"
                  >
                    {error?.message || "Failed to fetch videos"}
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && videos.length === 0 && !error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    No results
                  </td>
                </tr>
              )}

              {videos.map((video, index) => (
                <tr
                  key={video.id}
                  className={index % 2 === 1 ? "bg-gray50" : ""}
                >
                  {visibleColumnDefs.map((col) => (
                    <React.Fragment key={col.key}>
                      {col.renderCell(video, columnHandlers)}
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Divider color="gray200" type="horizontal" />

        <Pagination
          currentPage={pagination.page}
          pageNumber={totalPages}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPage={pagination.limit}
          showItemsPerPage={videos.length > 0}
          size="lg"
          className="px-6 py-4"
        />
      </div>
    </>
  );
}
